import React, { useState, useRef, useEffect } from "react";
import "./createPassword.css";
import { IoMdLock, IoMdCheckmarkCircle } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { Eye, EyeOff, Lock } from "lucide-react";
import { MdCancel } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import password_mark from "../../assets/password_mark.png";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, extra = {}) => {
  const headers = {
    "Content-Type": "application/json",
    "User-Timezone": getUserTimeZone(),
    ...extra,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

const clearOnboardingState = () => {
  localStorage.removeItem("onboarding_code");
  localStorage.removeItem("onboarding_completed");
  localStorage.removeItem("booking_settings");
  localStorage.removeItem("selectedNiche");
  localStorage.removeItem("selectedNicheId");
  localStorage.removeItem("selectedNicheName");
  localStorage.removeItem("selectedSubNiches");
  localStorage.removeItem("selectedServicesPayload");
  localStorage.removeItem("salonData");
  localStorage.removeItem("real_estate_mode");
  localStorage.removeItem("working_days_closed");
  localStorage.removeItem("booking_settings_closed");
  localStorage.removeItem("my_services_closed");
  localStorage.removeItem("suggested_services_closed");
  localStorage.removeItem("worker_biography_saved");
  localStorage.removeItem("worker_name");
  localStorage.removeItem("worker_phone");
  localStorage.removeItem("worker_country");

  sessionStorage.removeItem("onboarding_force_route");
  sessionStorage.removeItem("onboarding_force_status");
};

export default function CreatePassword() {
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const email = localStorage.getItem("pendingEmail");

  const registrationStartedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem("user_timezone", getUserTimeZone());
  }, []);

  const gtagSafe = (event, params) => {
    if (window.gtag) window.gtag("event", event, params);
  };

  const trackStartRegistration = () => {
    if (!registrationStartedRef.current) {
      gtagSafe("start_form_registration");
      registrationStartedRef.current = true;
    }
  };

  const trackRegistrationError = () => gtagSafe("registration_form_error");
  const trackSignInError = () => gtagSafe("sign_in_form_error");
  const trackSignUp = () => gtagSafe("sign_up", { method: "Email" });
  const trackLogin = () => gtagSafe("login", { method: "Email" });

  const handlePasswordChange = (e) => {
    const value = e.target.value;

    if (/\s/.test(value)) {
      const passwordWithoutSpaces = value.replace(/\s/g, "");
      setPassword(passwordWithoutSpaces);

      if (value.trim() !== value) toast.error("Spaces are not allowed in password");
      return;
    }

    setPassword(value);
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      toast.error("Spaces are not allowed in password");
    }
  };

  const handleRepeatPasswordChange = (e) => {
    const value = e.target.value;

    if (/\s/.test(value)) {
      const passwordWithoutSpaces = value.replace(/\s/g, "");
      setRepeatPassword(passwordWithoutSpaces);

      if (value.trim() !== value) toast.error("Spaces are not allowed in password");
      return;
    }

    setRepeatPassword(value);
  };

  const handleRepeatPasswordKeyDown = (e) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      toast.error("Spaces are not allowed in password");
    }
  };

  const conditions = [
    {
      label: "8 to 20 characters",
      valid: password.length >= 8 && password.length <= 20,
    },
    {
      label: "At least 1 number",
      valid: /\d/.test(password) && !/\s/.test(password),
    },
    {
      label: "At least 1 upper case letter",
      valid: /[A-Z]/.test(password) && !/\s/.test(password),
    },
    {
      label: "No spaces allowed",
      valid: !/\s/.test(password),
    },
  ];

  const isFormValid =
    conditions.every((c) => c.valid) &&
    password === repeatPassword &&
    !/\s/.test(password) &&
    !/\s/.test(repeatPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid || !email) {
      trackRegistrationError();
      toast.error("Please fill all fields correctly.");
      return;
    }

    if (/\s/.test(password) || /\s/.test(repeatPassword)) {
      trackRegistrationError();
      toast.error("Password cannot contain spaces");
      return;
    }

    setLoading(true);

    try {
      const setPasswordUrl = `${BASE_URL}/auth/public-set-password/`;
      const res = await fetch(setPasswordUrl, {
        method: "POST",
        headers: buildHeaders(null),
        body: JSON.stringify({
          email,
          password,
          repeat_password: repeatPassword,
          timeZone: getUserTimeZone(),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        trackRegistrationError();
        toast.error(data?.detail || "Password setup failed.");
        return;
      }

      trackSignUp();
      toast.success("Password created successfully!");
      localStorage.setItem("user_email", email);

      const tokenObtainUrl = `${BASE_URL}/auth/token/obtain/`;
      const loginRes = await fetch(tokenObtainUrl, {
        method: "POST",
        headers: buildHeaders(null),
        body: JSON.stringify({
          email,
          password,
          timeZone: getUserTimeZone(),
        }),
      });

      const loginData = await loginRes.json().catch(() => ({}));

      if (!loginRes.ok || !loginData?.access) {
        trackSignInError();
        toast.error("Login failed. Please try again.");
        return;
      }

      clearOnboardingState();

      trackLogin();

      localStorage.setItem("accessToken", loginData.access);
      localStorage.setItem("refreshToken", loginData.refresh);
      localStorage.setItem("user_status", loginData.user_status);
      localStorage.setItem("user_timezone", getUserTimeZone());

      try {
        const userInfoUrl = `${BASE_URL}/auth/userinfo/`;
        const userRes = await fetch(userInfoUrl, {
          headers: buildHeaders(loginData.access, { "Content-Type": undefined }),
        });

        if (userRes.ok) {
          const userData = await userRes.json();
          localStorage.setItem("user_info", JSON.stringify(userData));

          if (userData.currency) {
            localStorage.setItem("user_currency", userData.currency);
          }
        }
      } catch (userInfoErr) {
        console.warn("Could not fetch user info:", userInfoErr);
      }

      if (loginData.user_status === "worker") {
        localStorage.setItem("user_status", "worker");
        localStorage.setItem("onboarding_code", "234");
        sessionStorage.setItem("onboarding_force_route", "/worker-biography");
        sessionStorage.setItem("onboarding_force_status", "worker");

        navigate("/worker-biography", { replace: true });
        return;
      }

      navigate("/account-type-select", { replace: true });
    } catch (err) {
      trackRegistrationError();
      console.error("❌ CREATE PASSWORD ERROR:", err);
      toast.error("Network error. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && isFormValid && !loading) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [isFormValid, loading]);

  return (
    <div className="password">
      <main className="password-main">
        <div className="password-card">
          <img src={password_mark} alt="password" className="password-avatar" />

          <h2 className="password-title">Create Password</h2>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <label className="password-label">Password *</label>

            <div className="input-wrapper">
              <Lock className="icon" />
              <input
                type={showPass ? "text" : "password"}
                value={password}
                placeholder="Enter password"
                onChange={(e) => {
                  trackStartRegistration();
                  handlePasswordChange(e);
                }}
                onKeyDown={(e) => {
                  handlePasswordKeyDown(e);
                  if (e.key === "Enter" && isFormValid && !loading) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                required
                autoFocus
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowPass((v) => !v)}
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {password && (
              <ul className="password-conditions">
                {conditions.map((c, i) => (
                  <li key={i} className={c.valid ? "valid" : "invalid"}>
                    {c.valid ? <IoMdCheckmarkCircle /> : <MdCancel />} {c.label}
                  </li>
                ))}
              </ul>
            )}

            <label className="password-label">Repeat Password *</label>

            <div className="input-wrapper">
              <Lock className="icon" />
              <input
                type={showRepeat ? "text" : "password"}
                value={repeatPassword}
                placeholder="Repeat password"
                onChange={handleRepeatPasswordChange}
                onKeyDown={(e) => {
                  handleRepeatPasswordKeyDown(e);
                  if (e.key === "Enter" && isFormValid && !loading) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                required
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowRepeat((v) => !v)}
                aria-label={showRepeat ? "Hide repeat password" : "Show repeat password"}
              >
                {showRepeat ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button type="submit" className="password-btn" disabled={!isFormValid || loading}>
              {loading ? "Processing..." : "Next"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}